import { ref, watch } from 'vue'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'

const compareVersions = (left = '', right = '') => {
    const leftParts = String(left)
        .split('.')
        .map((part) => Number(part) || 0)
    const rightParts = String(right)
        .split('.')
        .map((part) => Number(part) || 0)
    const maxLength = Math.max(leftParts.length, rightParts.length)

    for (let index = 0; index < maxLength; index += 1) {
        const leftPart = leftParts[index] ?? 0
        const rightPart = rightParts[index] ?? 0

        if (leftPart > rightPart) return 1
        if (leftPart < rightPart) return -1
    }

    return 0
}

export default function useAppUpdate({ metas, isReady }) {
    const shouldShowBanner = ref(false)
    const availableVersion = ref(null)
    const updateStoreUrl = ref(null)

    const reset = () => {
        shouldShowBanner.value = false
        availableVersion.value = null
        updateStoreUrl.value = null
    }

    const resolveUpdateStoreUrl = () => {
        switch (Capacitor.getPlatform()) {
            case 'android':
                return import.meta.env.VITE_APP_PLAYSTORE_URL || null
            case 'ios':
                return import.meta.env.VITE_APP_APPSTORE_URL || null
            default:
                return null
        }
    }

    const openUpdateStore = () => {
        const storeUrl = resolveUpdateStoreUrl()

        if (!storeUrl) {
            return
        }

        window.location.href = storeUrl
    }

    const sync = async () => {
        const isNativeApp = typeof Capacitor.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
        const platform = Capacitor.getPlatform()

        if (!isReady.value || !isNativeApp || (platform !== 'ios' && platform !== 'android')) {
            reset()
            return
        }

        const nextAvailableVersion = metas.value?.app_update?.[platform]?.availableVersion

        if (!nextAvailableVersion || typeof nextAvailableVersion !== 'string') {
            reset()
            return
        }

        try {
            const info = await CapacitorApp.getInfo()
            const installedVersion = info?.version

            if (!installedVersion) {
                reset()
                return
            }

            availableVersion.value = nextAvailableVersion
            updateStoreUrl.value = resolveUpdateStoreUrl()
            shouldShowBanner.value = compareVersions(installedVersion, nextAvailableVersion) < 0
        } catch {
            reset()
        }
    }

    watch(
        [isReady, () => metas.value?.app_update],
        () => {
            sync().catch(() => {
                reset()
            })
        },
        { immediate: true, deep: true }
    )

    return {
        shouldShowBanner,
        availableVersion,
        updateStoreUrl,
        openUpdateStore,
    }
}
